import React, { memo } from 'react';
import { View, Text, TouchableOpacity, Switch, StyleSheet } from 'react-native';
import { SVG_COMPONENTS } from '@/assets/admin';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { TagPill } from '../TagPill';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

export interface DesktopTagRowProps {
  tag: CorkBoardTag;
  isEditing?: boolean;
  onToggleEnabled: (tag: CorkBoardTag) => void;
  onEdit: (tag: CorkBoardTag) => void;
  onDelete: (tag: CorkBoardTag) => void;
  /** Drag-handle props from useSortable — spread onto the handle View. */
  dragHandleProps?: Record<string, unknown>;
}

export const DesktopTagRow: React.FC<DesktopTagRowProps> = memo(
  ({ tag, isEditing, onToggleEnabled, onEdit, onDelete, dragHandleProps }) => {
    const { redesign, semantic } = useAdminColors();
    const disabled = !tag.isEnabled;

    return (
      <View
        style={[
          styles.row,
          isEditing && {
            backgroundColor: redesign.accent.blueBg,
            borderLeftWidth: 2,
            borderLeftColor: PRIMITIVE_COLORS.resioBlue,
          },
        ]}
      >
        <View style={styles.handleContainer} {...(dragHandleProps ?? {})}>
          <SVG_COMPONENTS.DRAG_ICON width={14} height={14} color={redesign.text.subtle} />
        </View>

        <View style={styles.pillContainer}>
          <TagPill name={tag.name} color={tag.color} icon={tag.icon} disabled={disabled} />
          {disabled ? (
            <Text
              style={[
                styles.hiddenBadge,
                { color: redesign.text.subtle, backgroundColor: redesign.glass.buttonBg },
              ]}
            >
              HIDDEN
            </Text>
          ) : null}
        </View>

        <Switch
          value={tag.isEnabled}
          onValueChange={() => onToggleEnabled(tag)}
          trackColor={{ false: redesign.separator, true: redesign.accent.blueBg }}
          thumbColor="#FFFFFF"
          ios_backgroundColor={redesign.separator}
          accessibilityLabel={`${tag.isEnabled ? 'Disable' : 'Enable'} ${tag.name}`}
        />

        <View style={styles.actions}>
          <TouchableOpacity
            style={[
              styles.iconButton,
              {
                backgroundColor: redesign.glass.buttonBg,
                borderColor: redesign.card.border,
              },
            ]}
            onPress={() => onEdit(tag)}
            hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
            accessibilityLabel={`Edit ${tag.name}`}
            accessibilityRole="button"
          >
            <SVG_COMPONENTS.PENCIL width={14} height={14} color={PRIMITIVE_COLORS.resioBlue} />
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.iconButton,
              {
                backgroundColor: redesign.glass.buttonBg,
                borderColor: redesign.card.border,
              },
            ]}
            onPress={() => onDelete(tag)}
            hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
            accessibilityLabel={`Delete ${tag.name}`}
            accessibilityRole="button"
          >
            <SVG_COMPONENTS.TRASH width={14} height={14} color={semantic.status.error} />
          </TouchableOpacity>
        </View>
      </View>
    );
  },
);

DesktopTagRow.displayName = 'DesktopTagRow';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 10,
    marginBottom: 4,
    borderLeftWidth: 0,
  },
  handleContainer: {
    width: 18,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'grab' as any,
  },
  pillContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  hiddenBadge: {
    fontSize: 9,
    fontWeight: '600',
    letterSpacing: 0.5,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    overflow: 'hidden',
  },
  actions: {
    flexDirection: 'row',
    gap: 6,
  },
  iconButton: {
    width: 30,
    height: 30,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
});
